// ─────────────────────────────────────────────────────────────
// Sync Service — Realtime Database read/write
// ─────────────────────────────────────────────────────────────
import { ref, set, get, onValue, off } from 'firebase/database';
import { db }                          from '@/firebase';
import { getTripId, setTripState }     from '@/store/tripState';
import { toastError }                  from '@/ui/toast';
import { logError }                    from '@/services/logger.service';
import { getUser }                     from '@/store/tripState';
import type { Trip }                   from '@/types';

/** Returns true if the user is currently logged in */
function isAuthenticated(): boolean {
  return getUser() !== null;
}

export function setSyncStatus(ok: boolean): void {
  const dot = document.getElementById('sync-dot');
  const txt = document.getElementById('sync-txt');
  if (dot) dot.className     = ok ? 'dot' : 'dot off';
  if (txt) txt.textContent   = ok ? 'Sincronizado' : 'Salvando…';
}

export async function saveTrip(path: string, value: unknown): Promise<void> {
  const tripId = getTripId();
  if (!tripId) return;
  setSyncStatus(false);
  try {
    await set(ref(db, `trips/${tripId}/${path}`), value ?? null);
    setSyncStatus(true);
  } catch (e) {
    setSyncStatus(false);
    // Ignore errors that occur during logout — user is no longer authenticated
    if (!isAuthenticated()) return;
    const msg = e instanceof Error ? e.message : String(e);
    const isPerm = msg.toLowerCase().includes('permission');
    logError(isPerm ? 'permission_denied' : 'firebase_error', e, { path: `trips/${tripId}/${path}` });
    toastError('Erro ao salvar: ' + msg);
    throw e;
  }
}

export function saveTripField(field: keyof Trip, value: unknown): void {
  saveTrip(field as string, value).catch(() => void 0);
  if (field === 'name') {
    const navTitle = document.getElementById('nav-trip-title');
    if (navTitle) navTitle.textContent = String(value);
  }
}

let _unsubscribe: (() => void) | null = null;
let _subscribedTripId: string | null  = null;

export function subscribeToTrip(tripId: string, onUpdate: (trip: Partial<Trip>) => void): void {
  unsubscribeFromTrip();
  _subscribedTripId = tripId;
  const tripRef = ref(db, `trips/${tripId}`);
  const handler = onValue(tripRef,
    snap => {
      // Ignore callbacks for trips we've already unsubscribed from
      if (_subscribedTripId !== tripId) return;
      if (!isAuthenticated()) return;
      const data = snap.val() as Partial<Trip> | null;
      if (data) { setTripState(data); onUpdate(data); }
    },
    err => {
      // Silently ignore ALL errors when not authenticated or already unsubscribed
      if (!isAuthenticated() || _subscribedTripId !== tripId) return;
      const isPerm = err.message.toLowerCase().includes('permission');
      logError(isPerm ? 'permission_denied' : 'firebase_error', err, { tripId });
      toastError('Erro de sincronização: ' + err.message);
    }
  );
  _unsubscribe = () => {
    _subscribedTripId = null;
    off(tripRef, 'value', handler);
  };
}

export function unsubscribeFromTrip(): void {
  _subscribedTripId = null;
  if (_unsubscribe) { _unsubscribe(); _unsubscribe = null; }
}

export async function readOnce<T>(path: string): Promise<T | null> {
  try {
    const snap = await get(ref(db, path));
    return snap.exists() ? (snap.val() as T) : null;
  } catch (e) {
    toastError('Erro ao ler dados: ' + (e instanceof Error ? e.message : String(e)));
    return null;
  }
}
